/** Shared UI languages. The legal jurisdiction remains Polish in every locale. */
export const SUPPORTED_LANGUAGES = ['pl', 'uk', 'en', 'fr', 'bg', 'vi'] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number];

export const isSupportedLanguage = (value?: string | null): value is SupportedLanguageCode =>
  !!value && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

export const normalizeSupportedLanguage = (value?: string | null): SupportedLanguageCode | null => {
  const base = value?.split(/[-_]/)[0]?.toLowerCase();
  return isSupportedLanguage(base) ? base : null;
};

export const VIETNAMESE_LANDING_PATH = '/prawo-dla-wietnamczykow';

/** Explicit landing links take precedence over an earlier account preference. */
export const getLandingLanguage = (pathname: string): SupportedLanguageCode | null => {
  const path = pathname.replace(/\/$/, '');
  if (path === VIETNAMESE_LANDING_PATH) return 'vi';
  if (path === `${VIETNAMESE_LANDING_PATH}/po-polsku`) return 'pl';
  return null;
};

interface LanguagePreferences {
  explicit?: string | null;
  account?: string | null;
  cached?: string | null;
  source?: string | null;
  fallback: SupportedLanguageCode;
}

/** Explicit choices win; browser locale never changes the deployment default. */
export const resolveLanguagePreferences = (preferences: LanguagePreferences): SupportedLanguageCode => {
  const chosen = normalizeSupportedLanguage(preferences.explicit) || normalizeSupportedLanguage(preferences.account);
  if (chosen) return chosen;
  const cached = normalizeSupportedLanguage(preferences.cached);
  // Older non-Polish saved preferences remain valid. An unmarked Polish cache
  // may have come from automatic initialization, so use the domain fallback.
  if (cached && (preferences.source === 'account' || preferences.source === 'manual' ||
    (!preferences.source && cached !== 'pl'))) return cached;
  return preferences.fallback;
};

const rememberLanguage = (key: 'explicitLanguagePreference' | 'accountLanguagePreference', language: SupportedLanguageCode): void => {
  if (typeof window === 'undefined') return;
  const source = key === 'explicitLanguagePreference' ? 'manual' : 'account';
  try {
    localStorage.setItem(key, language);
    localStorage.setItem('preferredLanguage', language);
    localStorage.setItem('languagePreferenceSource', source);
  } catch { /* Cookies can still retain the choice when storage is blocked. */ }
  for (const [name, value] of [[key, language], ['preferredLanguage', language], ['languagePreferenceSource', source]]) {
    document.cookie = `${name}=${value}; path=/; max-age=31536000; SameSite=Lax`;
  }
};

export const hasExplicitLanguage = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (getLandingLanguage(window.location.pathname) ||
    isSupportedLanguage(new URLSearchParams(window.location.search).get('lang'))) return true;
  try {
    return isSupportedLanguage(localStorage.getItem('explicitLanguagePreference'));
  } catch { return false; }
};

/** A choice made on this device survives SPA navigation and account hydration. */
export const rememberExplicitLanguage = (language: SupportedLanguageCode): void => {
  rememberLanguage('explicitLanguagePreference', language);
};

/** Account hydration can supersede the automatic domain default. */
export const rememberAccountLanguage = (language: SupportedLanguageCode): void => {
  rememberLanguage('accountLanguagePreference', language);
};
